import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import tw from 'twin.macro';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import { BaseModal, ButtonV2, Title } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';

interface PutOfflineJobPositionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PutOfflineJobPositionSuccessModal({ isOpen, onClose }: PutOfflineJobPositionSuccessModalProps) {
  const { t } = useTranslation('job-positions-page');
  const router = useRouter();
  const { user } = useAuth();

  const isUsingPPU = user?.using_ppu_business_model === true;

  const handleOpenNewPosition = useCallback(() => {
    onClose();
    router.push('/employer/job-positions/onboarding');
  }, [onClose, router]);

  const handleViewPayments = useCallback(() => {
    onClose();
    router.push('/employer/payments-and-invoices');
  }, [onClose, router]);

  // PPU version - New business model
  if (isUsingPPU) {
    const titleEl = (
      <div tw="flex flex-col items-center gap-4">
        <CheckCircleIcon tw="w-12 h-12 text-green-600" />
        <Title
          content={t('put_offline_success_ppu_title')}
          fontSize="extraLarge"
          fontWeight="semibold"
          textColor="neutral-700"
          twStyle={tw`text-center leading-[30px] px-4 sm:px-0`}
        />
      </div>
    );

    const footerEl = (
      <div tw="flex flex-col justify-center items-center gap-4 w-full">
        <ButtonV2 onClick={onClose} fullwidth variant="primary">
          {t('put_offline_success_ppu_button')}
        </ButtonV2>
      </div>
    );

    return (
      <BaseModal
        showMobileGestures
        modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
        open={isOpen}
        onClose={() => onClose()}
        title={titleEl}
        footer={footerEl}
      >
        <div tw="flex flex-col justify-center items-center gap-5 w-full text-center mt-6">
          <p tw="text-base font-medium text-neutral-700">{t('put_offline_success_ppu_description')}</p>
          <p tw="text-base font-medium text-neutral-700">{t('put_offline_success_ppu_reminder')}</p>
        </div>
      </BaseModal>
    );
  }

  // Legacy version - Old business model with flexible slots

  const titleEl = (
    <div tw="flex flex-col items-center gap-4">
      <Title
        content={t('put_offline_success_title')}
        fontSize="extraLarge"
        fontWeight="semibold"
        textColor="neutral-700"
        twStyle={tw`text-center leading-[30px] px-4 sm:px-0`}
      />
    </div>
  );

  const footerEl = (
    <div tw="flex flex-col justify-center items-center gap-4 w-full">
      <ButtonV2 onClick={handleOpenNewPosition} fullwidth variant="primary">
        {t('put_offline_success_new_position_button')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={handleViewPayments}>
        {t('put_offline_success_payments_button')}
      </ButtonV2>
    </div>
  );

  return (
    <BaseModal
      showMobileGestures
      modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
      open={isOpen}
      onClose={() => onClose()}
      title={titleEl}
      footer={footerEl}
    >
      <div tw="flex flex-col justify-center items-center gap-5 w-full text-center mt-6">
        <div tw="space-y-2">
          <p tw="text-base font-medium text-neutral-700">
            {t('put_offline_success_description_part1')}
            <span tw="font-bold"> {t('put_offline_success_description_part2')}</span>
          </p>
          <p tw="text-base font-medium text-neutral-700">{t('put_offline_success_description_part3')}</p>
        </div>
      </div>
    </BaseModal>
  );
}

export default PutOfflineJobPositionSuccessModal;
